import React, { createContext, useState, useEffect, useContext } from 'react';
import { useConfig } from './useConfig';
import { useAuthentication } from './useAuthentication';
import { AxiosResponse } from 'axios';
import { API, BackendApi } from '../services/api/api';
import { createTypedLogger } from '../utils/typedLogger';

export interface ApiContextProps {
  api: BackendApi;
}

export const ApiContext = createContext<ApiContextProps | null>(null);

const logger = createTypedLogger('api');

export const ApiProvider = ({ children }: { children: React.ReactNode }) => {
  const [apiProps, setApiProps] = useState<ApiContextProps | null>(null);
  const [loading, setLoading] = useState(true);
  const config = useConfig();
  const { auth } = useAuthentication();

  useEffect(() => {
    const api = new API(config.api);
    api.axios.interceptors.request.use(
      request => {
        if (auth.getRawIdToken()) {
          request.headers.Authorization = `Bearer ${auth.getRawIdToken()}`;
        }
        logger.debug('api request made', { req: request });
        return request;
      },
      error => {
        const { request, message } = error;
        logger.error('api request error', { request, error: message });
        return Promise.reject(error);
      },
    );

    api.axios.interceptors.response.use(
      response => {
        logger.debug('api response received', { response });
        return response;
      },
      (error: { response: AxiosResponse; message: string }) => {
        const { response, message } = error;
        let apiresp = {};
        if (response) {
          apiresp = {
            status: response.status,
            statusText: response.statusText,
            data: response.data,
            headers: response.headers,
          };
        }
        logger.error('api response error', {
          response: apiresp,
          error: message,
        });
        return Promise.reject(error);
      },
    );

    setApiProps({
      api,
    });
    setLoading(false);
  }, [config, auth]);

  if (loading) {
    return null;
  }

  return <ApiContext.Provider value={apiProps}>{children}</ApiContext.Provider>;
};

export function useApi(): ApiContextProps {
  const context = useContext(ApiContext);
  return context || assertNever();
}

function assertNever(): never {
  throw Error('Cannot use useApi outside of ApiProvider');
}
